// =============================================================================
// SimSense Platform — Sign Up Page
// =============================================================================

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "../state/authStore";
import { parseApiError } from "../utils/parseApiError";
import { validateSignupInput } from "../utils/signupValidation";
import { useTauri } from "../hooks/useTauri";

export function SignUp() {
  const navigate = useNavigate();
  const isTauri = useTauri();
  const { register, error: authError } = useAuthStore();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientErrors, setClientErrors] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const nextClientErrors = validateSignupInput(email, password);
    setClientErrors(nextClientErrors);
    if (nextClientErrors.length > 0) return;
    setLoading(true);

    try {
      await register({
        email: email.trim(),
        password,
        display_name: displayName.trim(),
      });
      navigate("/");
    } catch (err: any) {
      setError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const serverError = clientErrors.length > 0 ? null : error || authError;
  const errorMessages = [...clientErrors, ...parseApiError(serverError)];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        background: "radial-gradient(circle at top right, #1e293b, #0a0e1a)",
        padding: isTauri ? "60px 24px 24px" : "24px",
      }}
    >
      {/* Desktop drag region — allows window dragging from the top strip */}
      {isTauri && (
        <div
          data-tauri-drag-region=""
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "48px",
            zIndex: 50,
            // @ts-ignore
            WebkitAppRegion: "drag",
          }}
        />
      )}
      {!isTauri && (
        <Link
          to="/"
          style={{
            position: "absolute",
            top: "20px",
            left: "24px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            textDecoration: "none",
            color: "var(--text2)",
            fontSize: "14px",
            fontWeight: 500,
            transition: "color 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text1)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text2)")}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Home
        </Link>
      )}
      <div
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: "12px",
          padding: "40px",
          maxWidth: "440px",
          width: "100%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <Link to="/" style={{ textDecoration: "none" }}>
            <h1
              style={{
                fontSize: "32px",
                fontWeight: 800,
                color: "var(--text1)",
                marginBottom: "8px",
                letterSpacing: "-0.025em",
              }}
            >
              SimSense
            </h1>
          </Link>
          <p style={{ color: "var(--text2)", fontSize: "15px" }}>
            Create your SimSense account
          </p>
        </div>

        {errorMessages.length > 0 && (
          <div
            style={{
              background: "rgba(239, 68, 68, 0.1)",
              border: "1px solid rgba(239, 68, 68, 0.2)",
              color: "#f87171",
              padding: "14px 16px",
              borderRadius: "8px",
              fontSize: "14px",
              marginBottom: "20px",
              lineHeight: 1.5,
            }}
          >
            {errorMessages.length === 1 ? (
              errorMessages[0]
            ) : (
              <ul style={{ margin: 0, paddingLeft: "18px", display: "flex", flexDirection: "column", gap: "4px" }}>
                {errorMessages.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label style={{ display: "block", color: "var(--text2)", fontSize: "13px", marginBottom: "6px", fontWeight: 600 }}>
              Email Address
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (clientErrors.length > 0) setClientErrors([]);
              }}
              style={{
                width: "100%",
                padding: "12px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid var(--border)",
                borderRadius: "8px",
                color: "var(--text1)",
                fontSize: "15px",
                outline: "none",
              }}
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label style={{ display: "block", color: "var(--text2)", fontSize: "13px", marginBottom: "6px", fontWeight: 600 }}>
              Display Name (Optional)
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid var(--border)",
                borderRadius: "8px",
                color: "var(--text1)",
                fontSize: "15px",
                outline: "none",
              }}
              placeholder="Racer123"
            />
          </div>

          <div>
            <label style={{ display: "block", color: "var(--text2)", fontSize: "13px", marginBottom: "6px", fontWeight: 600 }}>
              Password (min 8 chars, uppercase, digit, special)
            </label>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (clientErrors.length > 0) setClientErrors([]);
              }}
              style={{
                width: "100%",
                padding: "12px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid var(--border)",
                borderRadius: "8px",
                color: "var(--text1)",
                fontSize: "15px",
                outline: "none",
              }}
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "14px",
              background: "var(--accent)",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              marginTop: "8px",
              transition: "all 0.15s ease",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Creating Account..." : "Create Account"}
          </button>
          <p style={{ fontSize: "11px", color: "var(--text3)", marginTop: "12px", textAlign: "center" }}>
            By creating an account, you agree to our{" "}
            <Link to="/terms" style={{ color: "var(--text2)" }}>Terms of Service</Link>{" "}and{" "}
            <Link to="/privacy" style={{ color: "var(--text2)" }}>Privacy Policy</Link>.
          </p>
        </form>

        <div style={{ marginTop: "24px", textAlign: "center" }}>
          <p style={{ color: "var(--text2)", fontSize: "14px" }}>
            Already have an account?{" "}
            <Link to="/login" style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 600 }}>
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
